import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const Shell = ({ title, lead, children }) => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="w-full max-w-md">
      <h1 className="font-display text-3xl font-bold mb-1">{title}</h1>
      <p className="text-sm text-slate-400 mb-6">{lead}</p>
      {children}
      <div className="mt-6 text-sm text-slate-400"><Link to="/login" className="text-cyan-300 hover:underline" data-testid="pw-back-to-login">Back to sign in</Link></div>
    </div>
  </div>
);
const field = "apx-ring px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-800 outline-none w-full";
const label = "text-xs font-mono-ticker uppercase tracking-widest text-slate-500";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email: email.trim() }); setSent(true); }
    catch (err) { toast.error(apiError(err, "Could not send the reset email")); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot your password?" lead="Enter the email you signed up with. If an account exists, we'll send a single-use link that works for 30 minutes.">
      {sent ? (
        <div className="apx-card p-4 flex gap-3 items-start" data-testid="forgot-sent">
          <MailCheck className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div className="text-sm text-slate-300">If an account exists for <span className="font-mono-ticker">{email}</span>, a reset link is on its way. Check spam if it doesn't arrive within a few minutes. The link expires in 30 minutes.</div>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" data-testid="forgot-form">
          <label className="flex flex-col gap-1.5"><span className={label}>Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="forgot-email" autoComplete="email" />
          </label>
          <button type="submit" disabled={busy} className="btn-primary rounded-lg py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="forgot-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Send reset link
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated — sign in with your new password");
      nav("/login", { replace: true });
    } catch (err) { toast.error(apiError(err, "This reset link is invalid or has expired")); setBusy(false); }
  };
  if (!token) return <Shell title="Reset link missing" lead="Open the link from your email, or request a new one."><Link to="/forgot-password" className="text-cyan-300 hover:underline" data-testid="reset-request-new">Request a new link</Link></Shell>;
  return (
    <Shell title="Choose a new password" lead="At least 8 characters. Every other signed-in device will be signed out.">
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="reset-form">
        <label className="flex flex-col gap-1.5"><span className={label}>New password</span>
          <input type="password" required minLength={8} maxLength={128} value={pw} onChange={(e) => setPw(e.target.value)} className={field} data-testid="reset-password" autoComplete="new-password" />
        </label>
        <label className="flex flex-col gap-1.5"><span className={label}>Repeat new password</span>
          <input type="password" required minLength={8} maxLength={128} value={pw2} onChange={(e) => setPw2(e.target.value)} className={field} data-testid="reset-password-2" autoComplete="new-password" />
        </label>
        <button type="submit" disabled={busy} className="btn-primary rounded-lg py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Set new password
        </button>
      </form>
    </Shell>
  );
}
